import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import type { To } from 'react-router';

import { Link } from '@sorare/routing';

import { LinkOther, LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';

import { LineupHeader } from 'components/lineup/LineupHeader';
import { isFixtureOpened } from 'lib/so5';

import { HideTeamDialog } from '../HideTeamDialog';
import { LineupActionsDropdown } from './LineupActionsDropdown';
import type { LineupLayoutHeader_so5LeaderboardContender } from './__generated__/index.graphql';

type Props = {
  to?: To;
  onClick?: () => void;
  state?: Record<string, unknown>;
  so5LeaderboardContender: Nullable<LineupLayoutHeader_so5LeaderboardContender>;
  rankingOnTop?: boolean;
  hideLineupsCount?: Nullable<boolean>;
  small?: boolean;
  disableLineupViewSelector?: boolean;
};

export const LineupLayoutHeader = ({
  to,
  onClick,
  state,
  so5LeaderboardContender,
  rankingOnTop,
  hideLineupsCount,
  small,
  disableLineupViewSelector,
}: Props) => {
  const { isMe } = useCurrentUserContext();
  const [hideTeamDialogOpened, setHideTeamDialogOpened] = useState(false);
  const { so5Leaderboard, managerTeam, so5Lineup } =
    so5LeaderboardContender || {};
  const isUpcoming =
    so5Leaderboard?.so5Fixture && isFixtureOpened(so5Leaderboard?.so5Fixture);
  const hasHideTeamButton =
    !!managerTeam &&
    isMe(managerTeam.user?.slug) &&
    isUpcoming &&
    managerTeam.hidable;
  const hasEditLineupButton = so5Lineup?.canEdit;
  return (
    <>
      <LinkOverlay
        to={to}
        as={to ? Link : 'span'}
        state={state}
        onClick={onClick}
      >
        <LineupHeader
          small={small}
          so5LeaderboardContender={so5LeaderboardContender}
          showLineupsCount={!rankingOnTop && !hideLineupsCount}
        />
      </LinkOverlay>
      {(!disableLineupViewSelector ||
        !!hasHideTeamButton ||
        !!hasEditLineupButton) && (
        <LinkOther as="div">
          <LineupActionsDropdown
            hasHideTeamButton={hasHideTeamButton}
            hasEditLineupButton={hasEditLineupButton}
            showHideTeamDialog={() => setHideTeamDialogOpened(true)}
            so5LeaderboardContender={so5LeaderboardContender}
          />
        </LinkOther>
      )}
      {!!hideTeamDialogOpened && !!so5LeaderboardContender?.managerTeam && (
        <HideTeamDialog
          onClose={() => setHideTeamDialogOpened(false)}
          managerTeam={so5LeaderboardContender?.managerTeam}
          so5Leaderboard={so5LeaderboardContender?.so5Leaderboard}
        />
      )}
    </>
  );
};

LineupLayoutHeader.fragments = {
  so5LeaderboardContender: gql`
    fragment LineupLayoutHeader_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Lineup {
        id
        canEdit
      }
      so5Leaderboard {
        slug
        so5Fixture {
          slug
          type
        }
        ...HideTeamDialog_so5Leaderboard
      }
      managerTeam {
        id
        hidable
        user {
          slug
        }
        ...HideTeamDialog_managerTeam
      }
      ...LobbyLineupHeader_so5LeaderboardContender
      ...LineupActionsDropdown_so5LeaderboardContender
    }
    ${LineupHeader.fragments.so5LeaderboardContender}
    ${HideTeamDialog.fragments.so5Leaderboard}
    ${HideTeamDialog.fragments.managerTeam}
    ${LineupActionsDropdown.fragments.so5LeaderboardContender}
  ` as TypedDocumentNode<LineupLayoutHeader_so5LeaderboardContender>,
};
